import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Trash2, ChevronLeft, ChevronRight, FileText, MessageSquare, Info, Download } from 'lucide-react';

interface PhotoField {
  id: string;
  fileName?: string;
  url?: string; // Supabase URL
  localPath?: string; // Temporary base64 during upload
  uploading?: boolean;
  error?: string;
  note?: string;
  caption?: string;
  timestamp?: string;
  order?: number;
}

interface PhotoGalleryModalProps {
  isOpen: boolean;
  onClose: () => void;
  photos: PhotoField[];
  sectionTitle: string;
  onDeletePhoto: (photoId: string) => void;
  onUpdateNote: (photoId: string, note: string) => void;
  onGalleryStateChange?: (isOpen: boolean) => void;
  zIndex?: number; // Optional custom z-index for proper layering
}

const PhotoGalleryModal: React.FC<PhotoGalleryModalProps> = ({
  isOpen,
  onClose,
  photos,
  sectionTitle,
  onDeletePhoto,
  onUpdateNote,
  onGalleryStateChange,
  zIndex = 9999 // Default z-index
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [editingNote, setEditingNote] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [showInfo, setShowInfo] = useState(false);

  // Notify parent when gallery opens/closes
  React.useEffect(() => {
    if (isOpen && photos.length > 0) {
      onGalleryStateChange?.(true);
    } else {
      onGalleryStateChange?.(false);
    }

    // Cleanup on unmount
    return () => {
      onGalleryStateChange?.(false);
    };
  }, [isOpen, photos.length, onGalleryStateChange]);

  if (!isOpen || photos.length === 0) return null;

  const currentPhoto = photos[currentIndex];

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % photos.length);
    setEditingNote(false);
  };

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + photos.length) % photos.length);
    setEditingNote(false);
  };

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this photo?')) {
      onDeletePhoto(currentPhoto.id);
      if (photos.length === 1) {
        onClose();
      } else if (currentIndex >= photos.length - 1) {
        setCurrentIndex(Math.max(0, currentIndex - 1));
      }
    }
  };

  const handleSaveNote = () => {
    onUpdateNote(currentPhoto.id, noteText);
    setEditingNote(false);
  };

  const handleStartEditNote = () => {
    setNoteText(currentPhoto.note || '');
    setEditingNote(true);
  };

  const handleDownload = async () => {
    const imageUrl = currentPhoto.url || currentPhoto.localPath;
    if (!imageUrl) return;

    try {
      // Fetch the image
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = currentPhoto.fileName || `photo-${currentIndex + 1}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download image:', error);
    }
  };

  const formatSectionTitle = (title: string): string => {
    // Convert snake_case or camelCase to Title Case
    return title
      .replace(/_/g, ' ')
      .replace(/([A-Z])/g, ' $1')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ')
      .trim();
  };

  return createPortal(
    <div className="fixed inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" style={{ zIndex }}>
      {/* Ambient backdrop blur effect */}
      <div className="absolute inset-0 backdrop-blur-3xl bg-white/5"></div>
      
      {/* Full-Screen Image Container */}
      <div className="relative w-full h-full flex items-center justify-center pt-12 pb-32">
        
        {/* Top Bar - Elegant Overlay */}
        <div 
          className="absolute top-0 left-0 right-0 z-20 bg-gradient-to-b from-slate-900/95 via-slate-900/70 to-transparent px-6 backdrop-blur-xl app-header-padding"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-3 px-4 py-2 bg-white/10 backdrop-blur-md rounded-full border border-white/20">
                <h2 className="text-base font-semibold text-white">{formatSectionTitle(sectionTitle)}</h2>
                <div className="w-px h-4 bg-white/30"></div>
                <span className="text-sm text-white/80 font-medium">{currentIndex + 1} of {photos.length}</span>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-3 bg-white/10 hover:bg-white/20 rounded-full transition-all backdrop-blur-md border border-white/20 hover:scale-110"
              title="Close gallery"
            >
              <X className="text-white" size={20} />
            </button>
          </div>
        </div>

        {/* Main Image - Full Screen with subtle shadow */}
        {/* Progressive Enhancement: Prefers CDN URL, falls back to compressed base64 for offline */}
        <img
          src={currentPhoto.url || currentPhoto.localPath || ''}
          alt={`Photo ${currentIndex + 1}`}
          className="max-w-[90%] max-h-[90%] object-contain rounded-2xl shadow-2xl"
        />
        
        {/* Navigation Arrows - Elegant Overlay */}
        {photos.length > 1 && (
          <>
            <button
              onClick={handlePrevious}
              className="absolute left-6 top-1/2 -translate-y-1/2 z-20 p-4 bg-white/10 hover:bg-white/20 backdrop-blur-xl text-white rounded-full shadow-2xl transition-all hover:scale-110 border border-white/20"
            >
              <ChevronLeft size={28} strokeWidth={2.5} />
            </button>
            <button
              onClick={handleNext}
              className="absolute right-6 top-1/2 -translate-y-1/2 z-20 p-4 bg-white/10 hover:bg-white/20 backdrop-blur-xl text-white rounded-full shadow-2xl transition-all hover:scale-110 border border-white/20"
            >
              <ChevronRight size={28} strokeWidth={2.5} />
            </button>
          </>
        )}

        {/* Action Buttons - Bottom Toolbar */}
        <div className="absolute bottom-0 left-0 right-0 z-20 pointer-events-none">
          <div className="flex items-center justify-between px-6 pb-4">
            {/* Left: Thumbnails spacer */}
            <div className="flex-1"></div>
            
            {/* Right: Action buttons */}
            <div className="flex items-center gap-3 pointer-events-auto">
              <button
                onClick={() => setShowInfo(!showInfo)}
                className={`flex items-center gap-2 px-4 py-3 rounded-full shadow-2xl transition-all backdrop-blur-xl border hover:scale-105 ${
                  showInfo 
                    ? 'bg-blue-500/90 hover:bg-blue-600 border-blue-400/50' 
                    : 'bg-white/10 hover:bg-white/20 border-white/20'
                } text-white`}
                title="Photo info & notes"
              >
                <MessageSquare size={18} />
                <span className="text-sm font-medium">Notes</span>
              </button>
              <button
                onClick={handleDownload}
                className="flex items-center gap-2 px-4 py-3 bg-white/10 hover:bg-white/20 rounded-full shadow-2xl transition-all backdrop-blur-xl text-white border border-white/20 hover:scale-105"
                title="Download photo"
              >
                <Download size={18} />
                <span className="text-sm font-medium">Download</span>
              </button>
              <button
                onClick={handleDelete}
                className="flex items-center gap-2 px-4 py-3 bg-gradient-to-br from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 rounded-full shadow-2xl transition-all backdrop-blur-xl text-white border border-red-400/30 hover:scale-105"
                title="Delete photo"
              >
                <Trash2 size={18} />
                <span className="text-sm font-medium">Delete</span>
              </button>
            </div>
          </div>
        </div>

        {/* Info Panel - Slide from Right */}
        <div className={`absolute top-0 right-0 h-full w-[420px] bg-gradient-to-br from-white via-slate-50/50 to-white backdrop-blur-3xl shadow-2xl transition-all duration-300 ease-out z-30 border-l border-slate-300/30 ${
          showInfo ? 'translate-x-0' : 'translate-x-full'
        }`}>
          <div className="flex flex-col h-full">
            {/* Panel Header */}
            <div className="px-8 py-6 border-b border-slate-200 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-white">Photo Details</h3>
                <button
                  onClick={() => setShowInfo(false)}
                  className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-all hover:scale-110 border border-white/20"
                >
                  <X size={20} className="text-white" />
                </button>
              </div>
            </div>

            {/* Panel Content */}
            <div className="flex-1 overflow-y-auto p-8 space-y-8">
              {/* Notes Section */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-base font-bold text-slate-900 flex items-center gap-2">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <MessageSquare size={18} className="text-blue-600" />
                    </div>
                    Notes
                  </h4>
                  {!editingNote && (
                    <button
                      onClick={handleStartEditNote}
                      className="px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white text-sm font-semibold rounded-lg transition-all shadow-sm hover:shadow-md"
                    >
                      {currentPhoto.note ? '✏️ Edit' : '➕ Add Note'}
                    </button>
                  )}
                </div>
                
                {editingNote ? (
                  <div className="space-y-4">
                    <textarea
                      value={noteText}
                      onChange={(e) => setNoteText(e.target.value)}
                      placeholder="Add detailed notes about this photo..."
                      className="w-full px-4 py-3 border-2 border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none text-base text-slate-800 leading-relaxed"
                      rows={8}
                      autoFocus
                    />
                    <div className="flex gap-3">
                      <button
                        onClick={handleSaveNote}
                        className="flex-1 px-5 py-3 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white text-sm font-semibold rounded-xl transition-all shadow-sm hover:shadow-md"
                      >
                        ✓ Save Note
                      </button>
                      <button
                        onClick={() => setEditingNote(false)}
                        className="flex-1 px-5 py-3 bg-slate-200 hover:bg-slate-300 text-slate-700 text-sm font-semibold rounded-xl transition-all"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="bg-white rounded-xl p-5 border-2 border-slate-200 min-h-[140px] shadow-sm">
                    {currentPhoto.note ? (
                      <p className="text-base text-slate-700 leading-relaxed whitespace-pre-wrap">{currentPhoto.note}</p>
                    ) : (
                      <div className="flex flex-col items-center justify-center h-full text-center py-6">
                        <MessageSquare size={32} className="text-slate-300 mb-2" />
                        <p className="text-sm text-slate-400">No notes added yet</p>
                        <p className="text-xs text-slate-400 mt-1">Click "Add Note" to get started</p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Photo Information */}
              <div>
                <h4 className="text-base font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <div className="p-2 bg-slate-100 rounded-lg">
                    <Info size={18} className="text-slate-600" />
                  </div>
                  Information
                </h4>
                <div className="bg-white rounded-xl border-2 border-slate-200 shadow-sm overflow-hidden">
                  <div className="divide-y divide-slate-200">
                    <div className="flex justify-between items-center px-5 py-4">
                      <span className="text-sm font-medium text-slate-600">Position</span>
                      <span className="text-sm font-bold text-slate-900 bg-slate-100 px-3 py-1 rounded-lg">{currentIndex + 1} of {photos.length}</span>
                    </div>
                    {currentPhoto.timestamp && (
                      <div className="flex justify-between items-center px-5 py-4">
                        <span className="text-sm font-medium text-slate-600">Captured</span>
                        <span className="text-sm font-semibold text-slate-900">
                          {new Date(currentPhoto.timestamp).toLocaleDateString('en-US', { 
                            month: 'short', 
                            day: 'numeric', 
                            year: 'numeric' 
                          })}
                        </span>
                      </div>
                    )}
                    {currentPhoto.uploading && (
                      <div className="flex justify-between items-center px-5 py-4 bg-blue-50">
                        <span className="text-sm font-medium text-slate-600">Status</span>
                        <span className="text-sm font-semibold text-blue-600 flex items-center gap-2">
                          <div className="w-2 h-2 bg-blue-600 rounded-full animate-pulse"></div>
                          Uploading...
                        </span>
                      </div>
                    )}
                    {currentPhoto.error && (
                      <div className="flex justify-between items-center px-5 py-4 bg-red-50">
                        <span className="text-sm font-medium text-slate-600">Status</span>
                        <span className="text-sm font-semibold text-red-600">Error</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Thumbnail Strip - Bottom Overlay */}
        {photos.length > 1 && (
          <div className="absolute bottom-20 left-0 right-0 z-10 bg-gradient-to-t from-slate-900/95 via-slate-900/70 to-transparent px-6 py-4 backdrop-blur-xl">
            <div className="flex gap-2 overflow-x-auto py-2 scrollbar-hide">
              {photos.map((photo, index) => (
                <button
                  key={photo.id}
                  onClick={() => {
                    setCurrentIndex(index);
                    setEditingNote(false);
                  }}
                  className={`relative flex-shrink-0 w-14 h-14 rounded-lg overflow-hidden transition-all ${
                    index === currentIndex
                      ? 'scale-105 opacity-100 ring-2 ring-white shadow-xl'
                      : 'ring-1 ring-white/30 hover:ring-white/50 hover:scale-105 opacity-60 hover:opacity-100 shadow-lg'
                  }`}
                >
                  <img
                    src={photo.url || photo.localPath || ''}
                    alt={`Thumbnail ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                  {photo.note && (
                    <div className="absolute top-1 right-1 bg-blue-500 rounded-full p-0.5">
                      <FileText size={8} className="text-white" />
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default PhotoGalleryModal;
